<template>
  <div class="customers">
    <div class="new-invoice concepts box wrapper-products">
      <div class="title-container is-flex">
        <h3 class="title is-centered is-4 has-text-primary title-products">
          Productos
        </h3>
        <div class="field is-centered">
          <BTooltip
            label="Agregar partida"
            type="is-success"
          >
            <a
              v-if="!disabled"
              class="button add-concept is-success is-rounded"
              @click="addRow"
            >
              <BIcon icon="plus" />
            </a>
          </BTooltip>
        </div>
      </div>
      <AppPagination
        class="is-centered"
        :current-page="currentPage"
        :buttons-max="buttonsMax"
        :pages-total="totalPages"
        @change="goToPage"
      />
      <BTable
        :data="concepts"
        :columns="columns"
      >
        <template
          slot="header"
          slot-scope="props"
        >
          <span :class="{ 'header-label': props.column.label === 'SKU' }">
            {{ props.column.label }}
          </span>
          <AppTooltip
            v-if="props.column.meta"
            :label="props.column.meta"
          />
        </template>
        <template slot-scope="props">
          <BTableColumn
            field="label"
            internal
          >
            <AppSelectSearch
              name="label"
              class="product-service-key-field"
              :class="{ 'selected-field': selected === `sku-${props.row.rowId}` }"
              placeholder="S012321A"
              :value="props.row.label"
              :disabled="isNew(props.row)"
              :taggable="true"
              required
              @input="onChange($event, props.row.rowId)"
              @change="onChangeIdentificationNumber($event, props.row.rowId)"
              @focus="onChangeSelected(`sku-${props.row.rowId}`)"
              @blur="onClearSelected"
            />
          </BTableColumn>
          <BTableColumn
            field="description"
            internal
          >
            <AppInputField
              :id="`description-${props.row.rowId}`"
              name="description"
              placeholder="Refrigerador"
              class="product-service-key-field"
              :class="{ 'selected-field': selected === `description-${props.row.rowId}` }"
              :value="props.row.description"
              :disabled="isLoading || disabled"
              :maxlength="1000"
              required
              @input="onChange($event, props.row.rowId)"
              @change="onChange($event, props.row.rowId)"
              @focus="onChangeSelected(`description-${props.row.rowId}`)"
              @blur="onClearSelected"
            />
          </BTableColumn>
          <BTableColumn
            field="productServiceKey"
            internal
          >
            <AppSelectSearch
              name="productServiceKey"
              class="product-service-key-field"
              :class="{ 'selected-field': selected === `product-service-key-${props.row.rowId}` }"
              placeholder="Clave de producto o servicio"
              :value="productServiceKeyObj(props.row.productServiceKey)"
              :options="productServiceKeyCatalog"
              :disabled="isLoading || disabled"
              :on-search="onSearch"
              required
              @input="onChange($event, props.row.rowId)"
              @change="onChange($event, props.row.rowId)"
              @focus="onChangeSelected(`product-service-key-${props.row.rowId}`)"
              @blur="onClearSelected"
            />
          </BTableColumn>
          <BTableColumn
            field="unitKey"
            internal
          >
            <AppSelectSearch
              name="unitKey"
              class="product-service-key-field"
              :class="{ 'selected-field': selected === `unit-key-${props.row.rowId}` }"
              placeholder="Clave de unidad"
              :options="unitKeyCatalog"
              :value="unitKeyObj(props.row.unitKey)"
              :disabled="isLoading || disabled"
              required
              @input="onChange($event, props.row.rowId)"
              @change="onChange($event, props.row.rowId)"
              @focus="onChangeSelected(`unit-key-${props.row.rowId}`)"
              @blur="onClearSelected"
            />
          </BTableColumn>
          <BTableColumn
            field="unit"
            internal
          >
            <AppInputField
              class="product-service-key-field"
              name="unitValue"
              placeholder="15.24"
              type="number"
              step="0.000001"
              min="0.000001"
              :disabled="isLoading || disabled"
              :value="props.row.unitValue"
              required
              @input="onChange($event, props.row.rowId)"
              @change="onChange($event, props.row.rowId)"
            />
          </BTableColumn>
          <BTableColumn
            field="actions"
            internal
          >
            <AppRadialMenu
              :size="36"
              :item-size="48"
              :radius="60"
              :angle-restriction="60"
              :rotate="-30"
            >
              <AppRadialMenuItem
                class="tooltip is-tooltip-success has-background-success has-text-white"
                data-tooltip="Guardar Producto"
              >
                <a
                  class="action-option"
                  @click="save(props.row)"
                >
                  <BIcon
                    icon="content-save"
                    class="action-option-icon"
                  />
                </a>
              </AppRadialMenuItem>
              <AppRadialMenuItem
                class="tooltip is-tooltip-danger has-background-danger has-text-white"
                data-tooltip="Eliminar Producto"
              >
                <a
                  class="action-option"
                  @click="deleteRow(props.row)"
                >
                  <BIcon
                    icon="delete"
                    class="action-option-icon"
                  />
                </a>
              </AppRadialMenuItem>
            </AppRadialMenu>
          </BTableColumn>
        </template>
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <BIcon
                  icon="emoticon-sad"
                  size="is-large"
                />
              </p>
              <p>No tienes ningún producto</p>
            </div>
          </section>
        </template>
      </BTable>
      <AppPagination
        class="is-centered"
        :current-page="currentPage"
        :buttons-max="buttonsMax"
        :pages-total="totalPages"
        @change="goToPage"
      />
    </div>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import debounce from 'lodash/debounce';

import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

import { keyUnit } from '@machete/common-catalogs';

export default {
  name: 'SkuCatalog',
  components: {
    AppTooltip: () => import(/* webpackChunkName: "app-tooltip" */ '@/components/AppTooltip.vue'),
    AppInputField: () => import(/* webpackChunkName: "app-input-field" */ '@/components/AppInputField'),
    AppSelectSearch: () => import(/* webpackChunkName: "app-select-search" */ '@/components/AppSelectSearch'),
    AppRadialMenu: () => import(/* webpackChunkName: "app-radial-menu" */ '@/components/AppRadialMenu.vue'),
    AppRadialMenuItem: () => import(/* webpackChunkName: "app-radial-menu-item" */ '@/components/AppRadialMenuItem.vue'),
    AppPagination: () => import(/* webpackChunkName: "app-input-rfc" */ '@/components/AppPagination.vue'),

  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: null,
      productServiceKeySearching: false,
      productServiceKeySearch: '',
      unitKeySearch: '',
      openedDetailed: [],
      skuSearch: {},
      newSku: false,
      buttonsMax: 5,
      columns: [
        {
          label: 'SKU',
          meta: 'Identificador de tu propio sistema de almacenamiento',
          field: 'identificationNumber',
          centered: true,
        },
        {
          label: 'Descripción',
          field: 'description',
          centered: true,
        },
        {
          label: 'Clave de producto o servicio',
          meta: 'Clave del producto correspondiente al cátalogo del SAT',
          field: 'productServiceKey',
          centered: true,
        },
        {
          label: 'Clave de unidad',
          meta: 'Clave de unidad de medida correspondiente al cátalogo del SAT',
          field: 'unitKey',
          centered: true,
        },
        {
          label: 'Valor unitario',
          field: 'unit',
          centered: true,
        },
        {
          label: '',
          field: 'actions',
          centered: true,
        },
      ],
      unitKeyCatalog: keyUnit.map(
        ({ key, value }) => ({ value: key, label: `${key} - ${value}` }),
      ),
      conceptTemplate: () => ({
        productServiceKey: '',
        label: '',
        unitKey: '',
        description: '',
        unitValue: null,
      }),
    };
  },
  computed: merge(
    mapState({
      productServiceKeyCatalog: ({ ui }) => ui.catalogs.productServiceKey,
      skuCatalog: ({ ui }) => ui.catalogs.sku,
      concepts: ({ ui }) => ui.catalogs.sku,
      companyId: state => state.auth.companyId,
      totalPages: state => state.ui.catalogs.totalPages,
    }),
    mapGetters({
      totalCurrency: 'invoice/totalCurrency',
      subTotalCurrency: 'invoice/subTotalCurrency',
      taxesCurrency: 'invoice/taxesCurrency',
    }),
    {
      unitKeyObj() {
        return (unitKey) => {
          const value = this.unitKeyCatalog.find(
            ku => ku.value === unitKey,
          );
          if (value) {
            return value;
          }
          return unitKey;
        };
      },

      productServiceKeyObj() {
        return (productServiceKey) => {
          const value = this.productServiceKeyCatalog.find(
            psk => psk.value === productServiceKey,
          );
          if (value) {
            return value;
          }
          return productServiceKey;
        };
      },

      descriptionCatalog() {
        const catalog = this.skuCatalog.map(
          ({ description, ...concept }) => ({
            ...concept,
            sku: concept.value,
            value: description,
            label: description,
          }),
        );

        return catalog;
      },

      lastPage() {
        return Math.ceil(this.invoices.length / this.itemsPerPage);
      },
      currentPage() {
        return parseInt(this.$route.query.page, 10);
      },
      size() {
        return this.$route.query.size;
      },
    },
  ),
  watch: {
    buttonsMax(newValue, oldValue) {
      if (newValue % 2) {
        return;
      }
      if (newValue < oldValue) {
        this.buttonsMax = newValue - 1;
      }
      if (newValue > oldValue) {
        this.buttonsMax = newValue + 1;
      }
    },
  },
  async beforeRouteUpdate(to, from, next, loading) {
    const { page = 1, size = 20 } = to.query;
    this.cleanState();
    this.searchSku(loading, '', this, page, size);
    next({ query: { page, size } });
  },
  async beforeMount(loading) {
    this.loading = true;
    await this.searchSku(loading, '', this);
    this.loading = false;
  },
  beforeRouteEnter(to, from, next) {
    if (to.query.page) next();
    else {
      next({ name: to.name, query: { page: 1, size: 20 } });
    }
  },
  methods: merge(
    mapMutations({
      setValue: 'ui/setValue',
      setValues: 'invoice/updateConceptValues',
      addConcept: 'invoice/addConcept',
      addSku: 'ui/addSku',
      deleteConcept: 'ui/deleteConcept',
      addConceptTax: 'invoice/addConceptTax',
      deleteConceptTax: 'invoice/deleteConceptTax',
      setUiValue: 'ui/setValue',
      cleanState: 'ui/cleanSKuCatalog',
      setSkuId: 'ui/setSkuId',
    }),
    mapActions({
      getCatalog: 'ui/getCatalog',
      getCatalogSku: 'ui/getCatalogSku',
      saveSku: 'ui/saveSku',
      delSku: 'ui/deleteSku',
    }),
    {
      addRow() {
        this.addSku(this.conceptTemplate());
      },

      isNew(product) {
        if (product.label === '') {
          return false;
        }
        return true;
      },

      goToPage(page) {
        const { size = 20 } = this.$route.query;

        const query = {
          page,
          size,
        };
        this.$router.push({ query });
      },

      deleteRow(row) {
        if (row.id) {
          this.delSku(row);
        }

        this.deleteConcept({ id: row.id });
      },

      async save(sku) {
        const productExist = this.concepts
          .find(product => product.label === sku.label && product.id !== sku.id);

        if (sku.label.trim() === '' || sku.description.trim() === '') {
          this.$store.dispatch('addTemporalNotification', {
            type: 'danger',
            content: 'Ingrese el SKU y la descripción del producto.',
          });

          return;
        }

        if (productExist) {
          this.$store.dispatch('addTemporalNotification', {
            type: 'danger',
            content: 'Ya existe un producto con el mismo SKU.',
          });

          return;
        }

        const payload = {
          productServiceKey: sku.productServiceKey,
          identificationNumber: sku.label,
          unitKey: sku.unitKey,
          description: sku.description,
          unit: sku.unitValue,
          id: sku.id,
        };

        delete payload.label;

        try {
          const { id } = await this.saveSku({ ...payload });
          this.setSkuId({ sku, id });

          this.$store.dispatch('addTemporalNotification', {
            type: 'success',
            content: 'Guardado correctamente',
          });
        } catch (error) {
          this.$store.dispatch('addTemporalNotification', {
            type: 'danger',
            content: 'Ocurrió un error al guardar.',
          });
        }
      },

      onChange(event, conceptId) {
        const { name, value } = event.target;

        const index = this.concepts.findIndex(({ rowId }) => rowId === conceptId);

        const accessor = `catalogs.sku.[${index}].${name}`;

        this.setValue({ accessor, value });
      },

      onChangeIdentificationNumber(event, conceptId) {
        const { value } = event.target;
        const index = this.concepts.findIndex(({ rowId }) => rowId === conceptId);
        this.setValue({ accessor: `catalogs.sku[${index}].label`, value });
      },

      onChangeProductServiceKey({ value }, index) {
        this.onChange({ target: { name: 'productServiceKey', value } }, index);
      },

      onChangeUnitKey({ value }, index) {
        this.onChange({ target: { name: 'unitKey', value } }, index);
      },

      getProductServicesKeys: debounce(async function getData() {
        this.productServiceKeySearching = true;

        await this.getCatalog({
          catalogName: 'productServiceKey',
          search: this.productServiceKeySearch,
        });

        this.productServiceKeySearching = false;
      }),

      onSearch(search, loading) {
        loading(true);

        this.search(loading, search, this);
      },

      searchSku: debounce(async (loading, search, vm, page = 1, size = 20) => {
        await vm.getCatalogSku({
          catalogName: `sku/company/${vm.companyId}`,
          query: {
            sku: search,
            page,
            size,
          },
        });

        loading(false);
      }, 650),

      search: debounce(async (loading, search, vm) => {
        await vm.getCatalog({ catalogName: 'productServiceKey', search });

        loading(false);
      }, 650),

      onChangeSelected(id) {
        this.selected = id;
      },

      onClearSelected() {
        this.selected = '';
      },
    },
  ),
};
</script>

<style scoped>
#app .b-table{
  overflow-x: visible;
}
.title.is-4 {
  margin-bottom: 0;
  padding-bottom: 1em;
}

.header-label {
  padding-right: 0.25rem;
}

.button.add-concept {
  padding-left: 0.75em!important;
  padding-right: 0.75em!important;
}

.button.add-concept .icon {
  margin-left: calc(-0.375em - 1px)!important;
  margin-right: calc(-0.375em - 1px)!important;
  font-size: inherit!important;
}

.add-concept-label {
  padding-left: .5rem;
}

.title-container {
  width: 100%;
  align-items: center;
  padding-bottom: .3rem;
  justify-content: space-between;
}

.product-service-key-field,
.product-service-key-field .field {
  width: 200px;
  max-width: 200px;
}

.selected-field.product-service-key-field,
.selected-field.product-service-key-field .field {
  width: 400px;
  max-width: 400px;
}

.unit-key-field,
.unit-key-field .field {
  width: 120px;
  max-width: 120px;
}

.selected-field.unit-key-field,
.selected-field.unit-key-field .field {
  width: 250px;
  max-width: 250px;
}

.description-field.selected-field {
  width: 400px;
}
</style>

<style>
.box.concepts th,
.box.concepts td {
  vertical-align: middle;
  text-align: center;
}

.box.concepts .label:not(:last-child) {
  margin-bottom: 0;
}

.new-invoice.concepts input[type='number'] {
  -moz-appearance: textfield;
}

.new-invoice.concepts input::-webkit-outer-spin-button,
.new-invoice.concepts input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.box.concepts .label:not(:last-child) {
  margin-right: 1rem;
}
</style>
